// Fully initialize the PATHS aggregator BEFORE any route module or page (all of
// which import PATHS) begins evaluating. Route files eager-import page
// components, and those pages import PATHS — so if a route file starts before
// paths.ts, paths.ts builds its object mid-cycle and hits a TDZ
// ("Cannot access 'X_PATHS' before initialization"). This ordered import pins
// paths.ts as the first routing module evaluated, keeping the cycle safe.
import "./paths";

import {
  Navigate,
  Outlet,
  createBrowserRouter,
  type RouteObject,
} from "react-router-dom";

import { preloadable as lazy } from "@/utils/lazyWithRetry";
import {
  RequireAuthMiddleware,
  RequireCompanyAccessMiddleware,
} from "../middleware";
import AuthLayout from "../pages/(auth)/Layout";
import Layout from "../pages/(home)/Layout";
import {
  CompanyDashboard,
  CompanySettings,
  Home,
  MaintainanceModeFallback,
} from "../utils/lazyLoadComponents";
import { authRoutes } from "./auth";
import { standaloneRoutes } from "./common/routes.blank";
import { commonRoutes } from "./common/routes.common";
import { companyAIAssistantRoutes } from "./company/routes.ai-assistant";
import { companyAnnouncementsRoutes } from "./company/routes.announcement";
import { companyAssetsRoutes } from "./company/routes.asset";
import {
  companyPoliciesSettingsCategoryRoutes,
  companyPoliciesSettingsRoutes,
} from "./company/routes.company-policies-settings";
import { companyDocumentsRoutes } from "./company/routes.documents";
import { companyEmployeesRoutes } from "./company/routes.employees";
import { companyEventRoutes } from "./company/routes.events";
import { companyExpensesRoutes } from "./company/routes.expenses";
import { companySalesCommissionsRoutes } from "./company/routes.sales-commissions";
import { companyBiometricDevicesRoutes } from "./company/routes.biometric-devices";
import { companyDataTransferRoutes } from "./company/routes.data-transfer";
import { companyIntegrationsRoutes } from "./company/routes.integrations";
import { companyAuditLogsRoutes } from "./company/routes.audit-logs";
import { companyCameraRoutes } from "./company/routes.camera";
import { companyAddOnsRoutes } from "./company/routes.add-ons";
import { companyFilesRoutes } from "./company/routes.files";
import { companyHelpSupportRoutes } from "./company/routes.help-and-support";
import { companyLeavesManagementRoutes } from "./company/routes.leave-management";
import { companyMarketplaceRoutes } from "./company/routes.marketplace";
import { companyNfcRoutes } from "./company/routes.nfc";
import { companyNotificationsRoutes } from "./company/routes.notifications";
import { companyOffboardingRoutes } from "./company/routes.offboarding";
import { companyOnboardingRoutes } from "./company/routes.onboarding";
import { companySetupRoutes } from "./company/routes.company-setup";
import { companyPayrollRoutes } from "./company/routes.payroll";
import { companyPerformancetRoutes } from "./company/routes.performance";
import { companyProjectsRoutes } from "./company/routes.projects";
import { companyRecruitmentRoutes } from "./company/routes.recruitment";
import {
  companySettingsRoutes,
  companyTemplateBuilderRoutes,
  companyTemplateSigningRoutes,
} from "./company/routes.settings";
import {
  companyStudioBuilderRoutes,
  companyStudioDocumentsBuilderRoutes,
  companyStudioEmailTemplatesBuilderRoutes,
  companyStudioFormsBuilderRoutes,
  companyStudioRoutes,
} from "./company/routes.studio";
import { companyMyAppRoutes } from "./company/routes.my-app";
import BuilderLayout from "@/components/layout/BuilderLayout";
import { companySubscriptionRoutes } from "./company/routes.subscription";
import { companyTasksRoutes } from "./company/routes.tasks";
import { companyTeamsRoutes } from "./company/routes.teams";
import { companyTimeAttendanceRoutes } from "./company/routes.time-attendance";
import { companyDigitalIdRoutes } from "./company/routes.digital-id";
import { companyWorkflowsRoutes } from "./company/routes.workflow";
import { myspaceAIAssistantRoutes } from "./my-space/routes.ai-assistant";
import { myspaceChatRoutes } from "./my-space/routes.chat";
import { mySpaceAssetsRoutes } from "./my-space/routes.asset";
import { myspaceCompanyPoliciesRoutes } from "./my-space/routes.company-policies";
import { myspaceGuidesRoutes } from "./my-space/routes.guides";
import { companyGuidesRoutes } from "./company/routes.guides";
import { mySpaceDigitalIdRoutes } from "./my-space/routes.digital-id";
import { mySpaceDocumentsRoutes } from "./my-space/routes.documents";
import { myspaceEventRoutes } from "./my-space/routes.events";
import { mySpaceExpensesRoutes } from "./my-space/routes.expenses";
import { mySpaceSalesCommissionsRoutes } from "./my-space/routes.sales-commissions";
import { myspaceLeavesManagementRoutes } from "./my-space/routes.leave-management";
import { myspaceMyAppRoutes } from "./my-space/routes.my-app";
import { myspaceOffboardingRoutes } from "./my-space/routes.offboarding";
import { myspacePayrollRoutes } from "./my-space/routes.payroll";
import { myspacePerformanceRoutes } from "./my-space/routes.performance";
import { myspaceRecruitmentRoutes } from "./my-space/routes.recruitment";
import { myspaceSettingsRoutes } from "./my-space/routes.settings";
import { mySpaceTasksRoutes } from "./my-space/routes.tasks";
import { myspaceTeamsRoutes } from "./my-space/routes.teams";
import { myspaceTimeAttendanceRoutes } from "./my-space/routes.time-attendance";

const SharedAiChat = lazy(() => import("@/pages/(public)/shared-ai-chat"));

const IS_APP_IN_MAINTAINANCE_MODE =
  import.meta.env.VITE_IS_APP_IN_MAINTENANCE === "true";

const maintenanceRoute = createBrowserRouter([
  {
    path: "*",
    element: <MaintainanceModeFallback />,
  },
]);

export const appRouteTree: RouteObject[] = [
  {
    path: "/auth",
    element: <AuthLayout />,
    children: [
      {
        path: "",
        element: <Navigate to="login" replace />,
      },
      ...authRoutes,
    ],
  },
  {
    path: "/",
    element: (
      <RequireAuthMiddleware>
        <Outlet />
      </RequireAuthMiddleware>
    ),
    children: [
      {
        element: <Layout />,
        children: [
          //COMMON ROUTE
          ...commonRoutes,
          { path: "/", element: <Home /> },
          myspaceTeamsRoutes,
          myspaceEventRoutes,
          mySpaceAssetsRoutes,
          mySpaceExpensesRoutes,
          mySpaceSalesCommissionsRoutes,
          mySpaceTasksRoutes,
          mySpaceDigitalIdRoutes,
          myspaceSettingsRoutes,
          mySpaceDocumentsRoutes,
          myspacePerformanceRoutes,
          myspaceAIAssistantRoutes,
          myspaceChatRoutes,
          myspaceMyAppRoutes,
          myspaceTimeAttendanceRoutes,
          myspaceCompanyPoliciesRoutes,
          myspaceGuidesRoutes,
          myspaceLeavesManagementRoutes,
          myspacePayrollRoutes,
          myspaceOffboardingRoutes,
          myspaceRecruitmentRoutes,
          // ANY NEW MODULES THAT SHOULD WORK INSIDE SPLIT SCREEN, SHOULD BE ALSO ADDED IN split-screen-routes.tsx AS WELL
          {
            path: "company",
            element: (
              <RequireCompanyAccessMiddleware>
                <Outlet />
              </RequireCompanyAccessMiddleware>
            ),
            children: [
              {
                path: "",
                element: <CompanyDashboard />,
              },
              // NEW ROUTING SYSTEM
              companyTeamsRoutes,
              companyEventRoutes,
              companyAssetsRoutes,
              companyExpensesRoutes,
              companySalesCommissionsRoutes,
              companyBiometricDevicesRoutes,
              companyTasksRoutes,
              // Main Settings: its root route's `settingsSpace` handle makes
              // <Layout/> swap the main menu for the settings sidebar.
              companySettingsRoutes,
              companyEmployeesRoutes,
              companyProjectsRoutes,
              companyStudioRoutes,
              companyMyAppRoutes,
              companyWorkflowsRoutes,
              companyDocumentsRoutes,
              companyDataTransferRoutes,
              companyIntegrationsRoutes,
              companyAuditLogsRoutes,
              companyCameraRoutes,
              companyAddOnsRoutes,
              companyFilesRoutes,
              companyHelpSupportRoutes,
              companyGuidesRoutes,
              companySubscriptionRoutes,
              companyPerformancetRoutes,
              companyAnnouncementsRoutes,
              companyNotificationsRoutes,
              companyTimeAttendanceRoutes,
              companyDigitalIdRoutes,
              // Policy / category forms sit under /company/settings/company-policies
              // but are declared in their own file — wrap them in the settings
              // page frame, and tag them, so they keep the settings sidebar.
              {
                Component: CompanySettings,
                handle: { flushContent: true, settingsSpace: "company" },
                children: [
                  companyPoliciesSettingsRoutes,
                  companyPoliciesSettingsCategoryRoutes,
                ],
              },
              companyLeavesManagementRoutes,
              companyPayrollRoutes,
              companyMarketplaceRoutes,
              companyOffboardingRoutes,
              companyOnboardingRoutes,
              companySetupRoutes,
              companyRecruitmentRoutes,
              companyAIAssistantRoutes,
              companyNfcRoutes,
              // ANY NEW MODULES THAT SHOULD WORK INSIDE SPLIT SCREEN, SHOULD BE ALSO ADDED IN split-screen-routes.tsx AS WELL
            ],
          },
        ],
      },
      {
        // Full-screen company builders live outside <Layout/> but are still
        // /company/* routes, so they need the same company-access gate.
        element: (
          <RequireCompanyAccessMiddleware>
            <Outlet />
          </RequireCompanyAccessMiddleware>
        ),
        children: [
          // Signing canvas stays fully full-screen (its own chrome).
          ...companyTemplateSigningRoutes,
          // Builders keep the app top bar (sidebar hidden) via BuilderLayout.
          {
            element: <BuilderLayout />,
            children: [
              ...companyTemplateBuilderRoutes,
              ...companyStudioDocumentsBuilderRoutes,
              ...companyStudioFormsBuilderRoutes,
              ...companyStudioEmailTemplatesBuilderRoutes,
              ...companyStudioBuilderRoutes,
            ],
          },
        ],
      },
    ],
  },
  ...standaloneRoutes,
  {
    path: "share/ai/:token",
    Component: SharedAiChat,
  },
  {
    path: "*",
    element: <Navigate to="/" />,
  },
];

const appRoutes = createBrowserRouter(appRouteTree);

export const router = IS_APP_IN_MAINTAINANCE_MODE
  ? maintenanceRoute
  : appRoutes;
